import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { DateStatus } from '../types/groupBooking';

interface GroupCalendarProps {
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  dateStatuses: Record<string, DateStatus>;
  selectedDates: string[];
  onDateToggle: (date: string) => void;
  loading?: boolean;
  adminMode?: boolean;
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

const GroupCalendar: React.FC<GroupCalendarProps> = ({
  currentMonth,
  onMonthChange,
  dateStatuses,
  selectedDates,
  onDateToggle,
  loading = false,
  adminMode = false,
}) => {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const prevMonth = () => onMonthChange(new Date(year, month - 1, 1));
  const nextMonth = () => onMonthChange(new Date(year, month + 1, 1));

  const formatDate = (day: number) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const isToday = (day: number) => {
    const d = new Date(year, month, day);
    return d.getTime() === today.getTime();
  };

  const isPast = (day: number) => {
    const d = new Date(year, month, day);
    return d < today;
  };

  const getDateStyle = (dateStr: string, day: number) => {
    const status = dateStatuses[dateStr];
    const selected = selectedDates.includes(dateStr);
    const past = isPast(day);

    if (adminMode) {
      // 관리자 모드: 선택 ON/OFF가 유일한 시각 기준
      if (status === 'booked') return 'bg-indigo-100 text-indigo-400 cursor-not-allowed';
      if (past) return 'bg-gray-50 text-gray-300 cursor-not-allowed';
      // ON = 진한 초록, OFF = 흰색+점선 테두리
      return selected
        ? 'bg-green-600 text-white cursor-pointer'
        : 'bg-white text-gray-400 border-dashed border-gray-300 hover:bg-gray-50 cursor-pointer';
    }

    // 일반 사용자 모드
    if (past) return 'bg-gray-50 text-gray-300 cursor-not-allowed';

    if (status === 'available') {
      return selected
        ? 'bg-green-600 text-white ring-2 ring-green-700 shadow-md cursor-pointer'
        : 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100 cursor-pointer';
    }
    if (status === 'booked') {
      return 'bg-gray-200 text-gray-500 cursor-not-allowed';
    }
    if (status === 'pending') {
      return selected
        ? 'bg-yellow-500 text-white ring-2 ring-yellow-600 shadow-md cursor-pointer'
        : 'bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100 cursor-pointer';
    }
    return 'bg-white text-gray-300 cursor-not-allowed';
  };

  const getStatusLabel = (dateStr: string, day: number) => {
    const status = dateStatuses[dateStr];
    const selected = selectedDates.includes(dateStr);

    if (adminMode) {
      if (status === 'booked') return '예약확정';
      if (isPast(day)) return '';
      return selected ? '예약가능' : '예약불가';
    }

    if (status === 'available') return '예약가능';
    if (status === 'booked') return '예약완료';
    if (status === 'pending') return '승인대기';
    return '';
  };

  const isClickable = (dateStr: string, day: number) => {
    if (adminMode) {
      const status = dateStatuses[dateStr];
      return status !== 'booked' && !isPast(day);
    }
    const status = dateStatuses[dateStr];
    return !isPast(day) && (status === 'available' || status === 'pending');
  };

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= lastDate; d++) cells.push(d);

  // 관리자 모드: 범례 분기
  const adminLegend = (
    <div className="flex items-center justify-end gap-4 px-6 py-2.5 text-xs text-gray-500 border-b bg-gray-50/50">
      <span className="flex items-center gap-1.5">
        <span className="w-4 h-4 rounded bg-green-600 flex items-center justify-center">
          <CheckIcon className="w-3 h-3 text-white" />
        </span>
        <span className="font-medium text-gray-700">예약 열림</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-4 h-4 rounded bg-white border border-dashed border-gray-300"></span>
        <span className="font-medium text-gray-700">예약 닫힘</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-4 h-4 rounded bg-indigo-100 flex items-center justify-center">
          <LockClosedIcon className="w-2.5 h-2.5 text-indigo-400" />
        </span>
        <span className="font-medium text-gray-700">확정 (변경불가)</span>
      </span>
    </div>
  );

  const userLegend = (
    <div className="flex items-center justify-end gap-4 px-6 py-2 text-xs text-gray-500 border-b">
      <span className="flex items-center gap-1">
        <span className="w-3 h-3 rounded bg-green-100 border border-green-200"></span> 예약가능
      </span>
      <span className="flex items-center gap-1">
        <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200"></span> 승인대기
      </span>
      <span className="flex items-center gap-1">
        <span className="w-3 h-3 rounded bg-gray-200 border border-gray-300"></span> 예약완료
      </span>
      <span className="flex items-center gap-1">
        <span className="w-3 h-3 rounded bg-white border border-gray-200"></span> 예약불가
      </span>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* 헤더: 월 이동 */}
      <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b">
        <button
          onClick={prevMonth}
          className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-lg font-bold text-gray-800">
          {year}년 {month + 1}월
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ChevronRightIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* 범례 */}
      {adminMode ? adminLegend : userLegend}

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 border-b">
        {DAY_NAMES.map((name, i) => (
          <div
            key={name}
            className={`py-3 text-center text-sm font-semibold ${
              i === 0 ? 'text-red-400 bg-red-50/50' : i === 6 ? 'text-blue-400 bg-blue-50/50' : 'text-gray-600 bg-gray-50/50'
            }`}
          >
            {name}
          </div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400 text-sm">로딩 중...</div>
        </div>
      ) : (
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (day === null) {
              return <div key={`empty-${idx}`} className="min-h-[80px] sm:min-h-[100px] border-b border-r border-gray-100" />;
            }

            const dateStr = formatDate(day);
            const clickable = isClickable(dateStr, day);
            const selected = selectedDates.includes(dateStr);
            const dayOfWeek = (firstDay + day - 1) % 7;
            const status = dateStatuses[dateStr];
            const statusLabel = getStatusLabel(dateStr, day);

            return (
              <div
                key={day}
                onClick={() => clickable && onDateToggle(dateStr)}
                className={`min-h-[80px] sm:min-h-[100px] border-b border-r border-gray-100 p-1.5 sm:p-2 transition-all ${
                  getDateStyle(dateStr, day)
                } ${selected ? 'z-10' : ''}`}
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm sm:text-base font-medium ${
                        isToday(day) ? 'underline decoration-2' : ''
                      } ${
                        adminMode && selected ? '' :
                        dayOfWeek === 0 ? 'text-red-400' : dayOfWeek === 6 ? 'text-blue-400' : ''
                      } ${selected ? '!text-white' : ''}`}
                    >
                      {day}
                    </span>
                    {/* 관리자: 선택된 날짜에 체크 아이콘 */}
                    {adminMode && selected && !isPast(day) && status !== 'booked' && (
                      <CheckIcon className="w-4 h-4 text-white/80" />
                    )}
                    {/* 관리자: 확정 건에 자물쇠 아이콘 */}
                    {adminMode && status === 'booked' && (
                      <LockClosedIcon className="w-3.5 h-3.5 text-indigo-400" />
                    )}
                  </div>
                  {statusLabel && (
                    <span className={`text-[10px] sm:text-xs mt-auto font-medium ${
                      adminMode
                        ? selected ? 'text-white/70' : status === 'booked' ? 'text-indigo-400' : 'text-gray-300'
                        : selected ? 'text-white/80' : ''
                    }`}>
                      {statusLabel}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GroupCalendar;
